/**
 * Вложения. Загрузка и скачивание идут по HTTP, а не по WebSocket
 * (решение 1): бинарные потоки и подписанные ссылки — не задача сокета.
 *
 * Прежде у «Добавить» и «Скачать» не было обработчиков: файл нельзя было
 * ни приложить, ни открыть.
 */

import { TaskSection } from "@taskflow/contracts";
import { Paperclip } from "lucide-react";
import { useRef, useState } from "react";

import type { WsError } from "@taskflow/client-core";
import { Button, ConfirmDialog, IconButton, toast } from "@/shared/ui";
import { Download, Trash2, Upload } from "@/shared/ui/motionIcons";

import { type TaskDetail, useDeleteAttachmentMutation } from "@taskflow/client-core/tasks";
import { AttachmentConsole } from "./AttachmentConsole";
import { AttachmentHar } from "./AttachmentHar";
import { AttachmentImage } from "./AttachmentImage";
import { AttachmentVideo } from "./AttachmentVideo";
import { Section } from "./Section";
import {
  ALLOWED_TYPES,
  isConsoleLog,
  isHarLog,
  isPlayableVideo,
  isPreviewableImage,
} from "../attachmentRules";
import { useAttachmentFiles } from "../useAttachmentFiles";

export function TaskAttachments({ task, canAdd }: { task: TaskDetail; canAdd: boolean }) {
  const [remove, { isLoading: removing }] = useDeleteAttachmentMutation();
  const { upload: uploadFile, download } = useAttachmentFiles(task.key);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const upload = async (file: File) => {
    setUploading(true);
    try {
      if (await uploadFile(file)) toast.success("Файл приложен", file.name);
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const confirmDelete = async () => {
    if (confirmId === null) return;
    try {
      await remove({ id: confirmId }).unwrap();
      setConfirmId(null);
      toast.success("Файл удалён");
    } catch (raw) {
      toast.error(raw as WsError);
    }
  };

  return (
    <Section
      title="Файлы"
      section={TaskSection.ATTACHMENTS}
      count={task.attachments.length}
      action={
        canAdd ? (
          <>
            <input
              ref={fileInput}
              type="file"
              className="hidden"
              aria-hidden
              tabIndex={-1}
              accept={ALLOWED_TYPES.join(",")}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void upload(file);
              }}
            />
            <Button
              size="sm"
              variant="secondary"
              icon={Upload}
              loading={uploading}
              onClick={() => fileInput.current?.click()}
            >
              Добавить
            </Button>
          </>
        ) : undefined
      }
    >
      {task.attachments.length === 0 ? (
        <p className="text-[13px] text-faint">Файлов нет</p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {task.attachments.map((file) => (
            <li
              key={file.id}
              className="flex items-center gap-2.5 rounded-md border border-line px-2.5 py-2"
            >
              {isPreviewableImage(file) ? (
                <AttachmentImage file={file} onDownload={() => void download(file.id)} />
              ) : isPlayableVideo(file) ? (
                <AttachmentVideo file={file} onDownload={() => void download(file.id)} />
              ) : isHarLog(file) ? (
                <AttachmentHar file={file} onDownload={() => void download(file.id)} />
              ) : isConsoleLog(file) ? (
                <AttachmentConsole file={file} onDownload={() => void download(file.id)} />
              ) : (
                <Paperclip className="size-4 shrink-0 text-faint" aria-hidden />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-semibold text-fg">{file.filename}</p>
                <p className="text-[11px] text-faint">
                  {file.sizeLabel} · {file.uploadedByName} · {file.uploadedAtLabel}
                </p>
                {file.isRecording === true && (
                  <p className="text-[11px] text-warning">
                    Видео записи бага удалится при закрытии задачи — логи останутся
                  </p>
                )}
              </div>
              <IconButton
                icon={Download}
                label={`Скачать ${file.filename}`}
                size="sm"
                onClick={() => void download(file.id)}
              />
              {file.canDelete && (
                <IconButton
                  icon={Trash2}
                  label={`Удалить ${file.filename}`}
                  size="sm"
                  variant="danger"
                  onClick={() => setConfirmId(file.id)}
                />
              )}
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={confirmId !== null}
        title="Удалить файл"
        description="Файл будет удалён без возможности восстановления."
        confirmLabel="Удалить"
        tone="danger"
        loading={removing}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setConfirmId(null)}
      />
    </Section>
  );
}
